import {parse, ParseError} from "./parser";
import {generateJs, CodegenError} from "./codegen";
import {LexError} from "./lexer";
import {
    fmtChar,
    fmtDouble,
    intDiv,
    intMod,
    JavaRuntimeError,
    makePrinter,
    makeScannerFactory,
    makeStepGuard,
    StepLimitExceededError,
    toInt,
} from "./runtime";

export interface TranspileResult {
    ok: boolean;
    js: string | null;
    error: { message: string; line: number | null } | null;
}

export function transpileJava(source: string): TranspileResult {
    try {
        const program = parse(source);
        const js = generateJs(program);
        return {ok: true, js, error: null};
    } catch (e) {
        if (e instanceof ParseError || e instanceof LexError || e instanceof CodegenError) {
            return {ok: false, js: null, error: {message: e.message, line: e.line}};
        }
        return {
            ok: false,
            js: null,
            error: {message: e instanceof Error ? e.message : String(e), line: null},
        };
    }
}

export interface RunResult {
    ok: boolean;
    output: string;
    error: { type: string; message: string } | null;
}

export interface RunOptions {
    maxSteps?: number;
    stdin?: string;
}

export function runGeneratedJs(js: string, options: RunOptions = {}): RunResult {
    const maxSteps = options.maxSteps ?? 200_000;
    const printer = makePrinter();
    const __step = makeStepGuard(maxSteps);
    const __newScanner = makeScannerFactory(options.stdin ?? "");

    try {
        // eslint-disable-next-line no-new-func
        const runner = new Function(
            "__println",
            "__print",
            "__step",
            "__fmtDouble",
            "__fmtChar",
            "__intDiv",
            "__intMod",
            "__int",
            "__newScanner",
            js
        );
        runner(
            printer.println,
            printer.print,
            __step,
            fmtDouble,
            fmtChar,
            intDiv,
            intMod,
            toInt,
            __newScanner
        );
        return {ok: true, output: printer.getOutput(), error: null};
    } catch (e) {
        if (e instanceof StepLimitExceededError) {
            return {
                ok: false,
                output: printer.getOutput(),
                error: {type: "Timeout", message: e.message},
            };
        }
        if (e instanceof JavaRuntimeError) {
            return {
                ok: false,
                output: printer.getOutput(),
                error: {type: e.javaType, message: e.message},
            };
        }
        return {
            ok: false,
            output: printer.getOutput(),
            error: {type: "RuntimeError", message: e instanceof Error ? e.message : String(e)},
        };
    }
}

export function runJava(source: string, options: RunOptions = {}): RunResult {
    const transpiled = transpileJava(source);
    if (!transpiled.ok || !transpiled.js) {
        return {
            ok: false,
            output: "",
            error: {
                type: "CompileError",
                message: transpiled.error?.line
                    ? `Line ${transpiled.error.line}: ${transpiled.error.message}`
                    : transpiled.error?.message ?? "Unknown compile error",
            },
        };
    }
    return runGeneratedJs(transpiled.js, options);
}
